import { useEffect } from 'react';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import { BookingCalendar } from '../home/components/BookingCalendar';

const TIMESLOTS_API =
  'https://readdy.ai/api/public/calendar/timeslots/9395c896-070b-4356-87d5-6717161b747b.670457f5037855118780bb2c7e1f4bd822f6755285fc48fb9798eacba5906de1';
const APPOINTMENTS_API =
  'https://readdy.ai/api/public/calendar/appointments/9395c896-070b-4356-87d5-6717161b747b.670457f5037855118780bb2c7e1f4bd822f6755285fc48fb9798eacba5906de1';

const TESTIMONIALS = [
  {
    name: 'Sarah K.',
    city: 'Carmel, IN',
    text: 'Booked online at 10pm. Benjamin called me at 9am the next morning. Had $750K coverage locked in before lunch. Could not be easier.',
    stars: 5,
  },
  {
    name: 'Mike & Amanda R.',
    city: 'Fishers, IN',
    text: 'Zero pressure. He compared 8 companies for us, saved us $18/month, and got us more coverage. Took exactly 22 minutes.',
    stars: 5,
  },
  {
    name: 'DeShawn T.',
    city: 'Noblesville, IN',
    text: 'I kept putting this off for years. Glad I finally did it. Simple, fast, honest. Wish I had done this at 30 instead of 40.',
    stars: 5,
  },
];

const CARRIERS = [
  'Mutual of Omaha', 'Protective Life', 'North American', 'Transamerica', 'Foresters',
  'Americo', 'Global Atlantic', 'Lincoln Financial',
];

const STEPS = [
  { n: '1', title: 'Pick your date & time', desc: 'Calendar below — 9AM to 9PM slots available.' },
  { n: '2', title: 'Enter your info', desc: 'Name, email, phone. Takes 60 seconds.' },
  { n: '3', title: 'We call you', desc: 'Benjamin calls at your exact time. No waiting.' },
  { n: '4', title: 'See your rates', desc: 'We compare 20+ carriers live on the call. Free.' },
];

export default function BookAppointment() {
  const SITE_URL = 'https://hoosierlifeinsurance.com';

  useEffect(() => {
    document.title = 'Book Your Free Life Insurance Consultation | Indianapolis, Indiana';

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!el) { el = document.createElement('meta'); el.setAttribute('name', name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };

    setMeta('description', 'Schedule your free life insurance consultation with a local Indiana agent. Phone, video, or in-person — same-day appointments available in Indianapolis. Call (317) 648-2983.');
    setMeta('keywords', 'book appointment, schedule consultation, life insurance appointment Indiana, free consultation, insurance agent Indianapolis');

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) { canonical = document.createElement('link'); canonical.setAttribute('rel', 'canonical'); document.head.appendChild(canonical); }
    canonical.setAttribute('href', `${SITE_URL}/book-appointment`);

    const schemaIds = ['schema-book-appt-service', 'schema-book-appt-breadcrumb'];
    schemaIds.forEach((id) => { const el = document.getElementById(id); if (el) el.remove(); });

    const serviceSchema = document.createElement('script');
    serviceSchema.id = 'schema-book-appt-service';
    serviceSchema.type = 'application/ld+json';
    serviceSchema.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Life Insurance Consultation Booking',
      description: 'Schedule a free consultation with a local Indiana life insurance agent',
      provider: { '@type': 'Organization', name: 'Hoosier Life Insurance', url: SITE_URL, telephone: '+13176482983' },
      areaServed: { '@type': 'State', name: 'Indiana' },
      serviceType: 'Insurance Consultation',
    });
    document.head.appendChild(serviceSchema);

    const breadcrumbSchema = document.createElement('script');
    breadcrumbSchema.id = 'schema-book-appt-breadcrumb';
    breadcrumbSchema.type = 'application/ld+json';
    breadcrumbSchema.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Book Appointment', item: `${SITE_URL}/book-appointment` },
      ],
    });
    document.head.appendChild(breadcrumbSchema);

    return () => { schemaIds.forEach((id) => { const el = document.getElementById(id); if (el) el.remove(); }); };
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-[#F7F9FB]">
      <Header />

      {/* ── Hero ── */}
      <section className="bg-gradient-to-br from-[#0F2A3F] via-[#1A3A52] to-[#0D2235] pt-28 pb-16 md:pt-36 md:pb-20 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#D97642]/10 rounded-full blur-[120px] -translate-y-1/3 translate-x-1/4" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#D97642]/8 rounded-full blur-[100px] translate-y-1/3 -translate-x-1/4" />
        </div>
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8 relative z-10 text-center">
          <div className="inline-flex items-center gap-2 bg-[#D97642]/20 border border-[#D97642]/30 rounded-full px-4 py-1.5 mb-5">
            <i className="ri-calendar-check-line text-[#F0A070] text-[14px]"></i>
            <span className="text-[#F0A070] text-[12px] font-semibold uppercase tracking-widest">Free Consultation — No Obligation</span>
          </div>

          <h1 className="text-[34px] sm:text-[46px] md:text-[56px] font-bold text-white leading-[1.1] mb-5">
            Pick a Time.<br />
            <span className="text-[#D97642]">We&apos;ll Call You.</span>
          </h1>

          <p className="text-[16px] md:text-[18px] text-white/75 max-w-[580px] mx-auto leading-[1.75] mb-6">
            15 minutes. 20+ carriers compared. Rates you can&apos;t get by calling companies directly. A local Indiana agent — not a call center.
          </p>

          {/* Stats strip */}
          <div className="flex flex-wrap items-center justify-center gap-6 mb-8">
            {[
              { value: '500+', label: 'Indiana Families Helped' },
              { value: '4.9★', label: 'Average Rating' },
              { value: '$0', label: 'Cost to You' },
              { value: '15 min', label: 'Average Call' },
            ].map(s => (
              <div key={s.label} className="text-center">
                <div className="text-[22px] font-black text-[#D97642]">{s.value}</div>
                <div className="text-[11px] text-white/60 uppercase tracking-wide">{s.label}</div>
              </div>
            ))}
          </div>

          {/* Meeting types */}
          <div className="flex flex-wrap justify-center gap-3">
            {[
              { icon: 'ri-phone-line', label: 'Phone Call' },
              { icon: 'ri-video-chat-line', label: 'Video Call' },
              { icon: 'ri-map-pin-line', label: 'In-Person' },
              { icon: 'ri-moon-line', label: 'Evening Slots' },
            ].map(b => (
              <div key={b.label} className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full border border-white/20">
                <div className="w-4 h-4 flex items-center justify-center"><i className={`${b.icon} text-[14px] text-[#D97642]`} /></div>
                <span className="text-[13px] text-white/90 font-medium">{b.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How It Works ── */}
      <section className="bg-white border-b border-gray-100 py-8">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {STEPS.map(s => (
              <div key={s.n} className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#D97642] text-white text-[13px] font-black flex-shrink-0 mt-0.5">{s.n}</div>
                <div>
                  <div className="text-[14px] font-bold text-[#1A3A52]">{s.title}</div>
                  <div className="text-[12px] text-[#718096] leading-snug">{s.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Main Content ── */}
      <section className="py-14 md:py-20 flex-1">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-start">

            {/* ── Left Panel ── */}
            <div className="lg:col-span-2 flex flex-col gap-5">

              {/* Social Proof — Testimonials */}
              <div className="bg-white rounded-2xl border border-gray-200 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <div className="flex">
                    {[1,2,3,4,5].map(s => <i key={s} className="ri-star-fill text-[#F59E0B] text-[15px]"></i>)}
                  </div>
                  <span className="text-[14px] font-bold text-[#1A3A52]">4.9 / 5</span>
                  <span className="text-[12px] text-[#718096]">(127 reviews)</span>
                </div>
                <div className="space-y-4">
                  {TESTIMONIALS.map((t, i) => (
                    <div key={i} className={`${i > 0 ? 'pt-4 border-t border-gray-100' : ''}`}>
                      <p className="text-[13px] text-[#4A5568] leading-[1.7] italic mb-2">&ldquo;{t.text}&rdquo;</p>
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-[12px] font-bold text-[#1A3A52]">{t.name}</div>
                          <div className="text-[11px] text-[#718096]">{t.city}</div>
                        </div>
                        <div className="flex">
                          {[1,2,3,4,5].map(s => <i key={s} className="ri-star-fill text-[#F59E0B] text-[11px]"></i>)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Call Us */}
              <div className="bg-gradient-to-br from-[#D97642] to-[#C26638] rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full flex-shrink-0">
                    <i className="ri-phone-fill text-[18px] text-white"></i>
                  </div>
                  <div>
                    <div className="text-[12px] text-white/70 font-medium">Prefer to call now?</div>
                    <a href="tel:+13176482983" className="text-[22px] font-bold text-white hover:text-white/90 cursor-pointer">(317) 648-2983</a>
                  </div>
                </div>
                <p className="text-[13px] text-white/70">Mon–Fri 9AM–6PM · Sat 10AM–2PM</p>
                <p className="text-[13px] text-white font-semibold mt-0.5">Evening video calls until 9 PM</p>
              </div>

              {/* Carriers */}
              <div className="bg-white rounded-2xl border border-gray-200 p-6">
                <h3 className="text-[14px] font-bold text-[#1A3A52] mb-3 flex items-center gap-2">
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-building-line text-[#D97642] text-[15px]"></i></div>
                  Carriers We Compare For You
                </h3>
                <div className="flex flex-wrap gap-2">
                  {CARRIERS.map(c => (
                    <span key={c} className="text-[11px] bg-gray-50 border border-gray-200 text-[#4A5568] px-2.5 py-1 rounded-full font-medium">{c}</span>
                  ))}
                  <span className="text-[11px] bg-gray-50 border border-gray-200 text-[#4A5568] px-2.5 py-1 rounded-full font-medium">+ 12 more</span>
                </div>
                <p className="text-[12px] text-[#718096] mt-3">Independent = we work for you, not one company.</p>
              </div>

              {/* Why book */}
              <div className="bg-white rounded-2xl border border-gray-200 p-6">
                <h3 className="text-[14px] font-bold text-[#1A3A52] mb-3 flex items-center gap-2">
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-shield-check-fill text-[#D97642] text-[16px]"></i></div>
                  Why Book With Benjamin?
                </h3>
                <ul className="space-y-2.5">
                  {[
                    'Licensed Indiana life insurance agent',
                    'Not a call center — one local agent',
                    'Compares 20+ A-rated carriers for you',
                    '100% free — never any cost to you',
                    'No pressure, no hard sell, ever',
                    'Evening & weekend appointments available',
                    'Response within 24 hours guaranteed',
                  ].map(item => (
                    <li key={item} className="flex items-start gap-2.5">
                      <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                        <i className="ri-check-line text-[#D97642] text-[14px]"></i>
                      </div>
                      <span className="text-[13px] text-[#4A5568]">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Service Areas */}
              <div className="bg-white rounded-2xl border border-gray-200 p-6">
                <h3 className="text-[14px] font-bold text-[#1A3A52] mb-3 flex items-center gap-2">
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-map-2-line text-[#D97642] text-[15px]"></i></div>
                  Areas We Serve
                </h3>
                <div className="flex flex-wrap gap-2">
                  {['Indianapolis', 'Carmel', 'Fishers', 'Noblesville', 'Greenwood', 'Avon', 'Brownsburg', 'Zionsville', 'Westfield', 'Anderson', 'All of Indiana'].map(city => (
                    <span key={city} className="px-2.5 py-1 bg-[#F7F9FB] text-[#4A5568] text-[11px] rounded-full border border-gray-200">{city}</span>
                  ))}
                </div>
              </div>
            </div>

            {/* ── Right — Calendar ── */}
            <div className="lg:col-span-3">
              {/* Urgency banner */}
              <div className="bg-[#1A3A52] rounded-xl px-5 py-3 mb-4 flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                  <i className="ri-calendar-line text-[#D97642] text-[20px]"></i>
                </div>
                <div>
                  <span className="text-white text-[13px] font-semibold">It takes 15 minutes.</span>
                  <span className="text-white/70 text-[13px]"> It costs nothing. </span>
                  <span className="text-[#D97642] text-[13px] font-semibold">Pick a time below.</span>
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-gray-200 p-6 md:p-8">
                <div className="mb-6">
                  <h2 className="text-[22px] font-bold text-[#1A3A52] mb-1">Book Your Free Consultation</h2>
                  <p className="text-[14px] text-[#718096]">Select a date, choose a time, fill in your info — we&apos;ll call you at your time.</p>
                </div>
                <BookingCalendar
                  timeslotsApi={TIMESLOTS_API}
                  appointmentsApi={APPOINTMENTS_API}
                  texts={{
                    heading: 'Book Your Free Consultation',
                    step0Title: 'Pick a Date',
                    step1Title: 'Choose a Time',
                    step2Title: 'Confirm Your Booking',
                    selectDate: 'Select Date',
                    selectTime: 'See Available Times',
                    reviewAndBook: 'Confirm My Appointment',
                    previousMonth: 'Previous',
                    nextMonth: 'Next',
                    noSlotsAvailable: 'No available slots for this date',
                    selectedDateTime: 'Your Appointment',
                    modifyTime: 'Change Time',
                    customerName: 'Your Full Name',
                    customerEmail: 'Email Address',
                    customerPhone: 'Phone Number',
                    notes: 'Anything we should know? (Optional)',
                    submitButton: 'Book My Free Consultation',
                    submitting: 'Booking...',
                    successMessage: "You\'re booked! Benjamin will call you at your scheduled time. Check your email for confirmation.",
                    errorMessage: 'Booking failed. Please try again or call (317) 648-2983.',
                    backButton: 'Back to Calendar',
                    required: '*',
                  }}
                  containerStyle=""
                />
              </div>

              {/* Trust badges */}
              <div className="mt-5 grid grid-cols-3 gap-4">
                {[
                  { icon: 'ri-lock-line', title: 'Secure & Private', desc: 'Your info is never shared or sold' },
                  { icon: 'ri-award-line', title: 'Indiana Licensed', desc: 'State licensed — verified & bonded' },
                  { icon: 'ri-time-line', title: 'Prompt Response', desc: 'Call back within 24 hours' },
                ].map(b => (
                  <div key={b.title} className="bg-white rounded-xl border border-gray-200 p-4 text-center">
                    <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
                      <i className={`${b.icon} text-[20px] text-[#D97642]`} />
                    </div>
                    <p className="text-[12px] font-bold text-[#1A3A52]">{b.title}</p>
                    <p className="text-[11px] text-[#718096] mt-0.5 leading-tight">{b.desc}</p>
                  </div>
                ))}
              </div>

              {/* Bottom social proof */}
              <div className="mt-5 bg-[#FDF6F0] border border-[#D97642]/20 rounded-xl p-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#1A3A52] rounded-full flex-shrink-0 text-white text-[13px] font-bold">BW</div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[14px] font-bold text-[#1A3A52]">Benjamin Wright</span>
                      <div className="flex">{[1,2,3,4,5].map(s => <i key={s} className="ri-star-fill text-[#F59E0B] text-[12px]"></i>)}</div>
                    </div>
                    <p className="text-[13px] text-[#4A5568] leading-[1.6] italic">
                      &ldquo;I&apos;ve helped over 500 Indiana families find the right coverage. Every call starts with listening — not selling. You&apos;ll know your options and the real cost in 15 minutes, with zero pressure to do anything.&rdquo;
                    </p>
                    <div className="text-[12px] text-[#718096] mt-2">Independent Agent · Indianapolis, IN · (317) 648-2983</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
